/**
 * The small controls the property panel is built from.
 *
 * Here rather than inline in the panel because there are forty-odd fields
 * across thirteen block types, and forty hand-rolled label-plus-input pairs is
 * how a panel ends up with three slightly different number inputs on one screen.
 *
 * The colour control is the one with an opinion in it: a block colour may be
 * EMPTY, meaning "whatever the letter uses". Storing a copy of the letter's
 * colour instead would freeze it, and changing the palette later would leave
 * every old block behind on the old value.
 */
import type { ReactNode } from "react";
import { RotateCcw } from "lucide-react";
import { AlignCenter, AlignLeft, AlignRight } from "lucide-react";
import { Input, Select, SelectContent, SelectItem, SelectTrigger, SelectValue, Switch, cn } from "@wa/ui";
import { useT } from "../../i18n/I18nProvider";
import type { Align } from "../types";

export function Row({ label, hint, children }: { label: string; hint?: string; children: ReactNode }) {
  return (
    <label className="block space-y-1">
      <span className="text-[11px] font-medium text-muted-foreground">{label}</span>
      {children}
      {hint && <span className="block text-[10px] leading-snug text-muted-foreground/70">{hint}</span>}
    </label>
  );
}

export function Group({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className="space-y-2.5 border-t border-border pt-3 first:border-t-0 first:pt-0">
      <h4 className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground/70">{title}</h4>
      {children}
    </section>
  );
}

export function TextField({
  label,
  hint,
  value,
  onChange,
  placeholder,
}: {
  label: string;
  hint?: string;
  value: unknown;
  onChange: (v: string) => void;
  placeholder?: string;
}) {
  return (
    <Row label={label} hint={hint}>
      <Input
        className="h-8 text-xs"
        value={String(value ?? "")}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
      />
    </Row>
  );
}

export function AreaField({
  label,
  hint,
  value,
  onChange,
  rows = 4,
  mono,
}: {
  label: string;
  hint?: string;
  value: unknown;
  onChange: (v: string) => void;
  rows?: number;
  mono?: boolean;
}) {
  return (
    <Row label={label} hint={hint}>
      <textarea
        rows={rows}
        value={String(value ?? "")}
        onChange={(e) => onChange(e.target.value)}
        className={cn(
          "w-full rounded-md border border-input bg-transparent px-2.5 py-1.5 text-xs leading-relaxed shadow-sm",
          "focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring",
          mono && "font-mono text-[11px]",
        )}
      />
    </Row>
  );
}

export function NumberField({
  label,
  hint,
  value,
  onChange,
  min = 0,
  max = 999,
  suffix,
}: {
  label: string;
  hint?: string;
  value: unknown;
  onChange: (v: number) => void;
  min?: number;
  max?: number;
  suffix?: string;
}) {
  return (
    <Row label={label} hint={hint}>
      <div className="relative">
        <Input
          type="number"
          className="h-8 pr-8 text-xs"
          min={min}
          max={max}
          value={Number(value ?? 0)}
          onChange={(e) => {
            const n = Number(e.target.value);
            onChange(Number.isFinite(n) ? Math.max(min, Math.min(max, n)) : min);
          }}
        />
        {suffix && (
          <span className="pointer-events-none absolute right-2.5 top-1/2 -translate-y-1/2 text-[10px] text-muted-foreground">
            {suffix}
          </span>
        )}
      </div>
    </Row>
  );
}

export function SwitchField({
  label,
  hint,
  value,
  onChange,
}: {
  label: string;
  hint?: string;
  value: unknown;
  onChange: (v: boolean) => void;
}) {
  return (
    <div className="flex items-start justify-between gap-3">
      <div className="min-w-0">
        <p className="text-[11px] font-medium text-muted-foreground">{label}</p>
        {hint && <p className="mt-0.5 text-[10px] leading-snug text-muted-foreground/70">{hint}</p>}
      </div>
      <Switch checked={value === true} onCheckedChange={onChange} />
    </div>
  );
}

export function ChoiceField({
  label,
  hint,
  value,
  options,
  onChange,
}: {
  label: string;
  hint?: string;
  value: unknown;
  options: { value: string; label: string }[];
  onChange: (v: string) => void;
}) {
  return (
    <Row label={label} hint={hint}>
      <Select value={String(value ?? "")} onValueChange={onChange}>
        <SelectTrigger className="h-8 text-xs">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {options.map((o) => (
            <SelectItem key={o.value} value={o.value} className="text-xs">
              {o.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </Row>
  );
}

const ALIGN_ICONS = { left: AlignLeft, center: AlignCenter, right: AlignRight } as const;

export function AlignField({ value, onChange }: { value: unknown; onChange: (v: Align) => void }) {
  const t = useT();
  const current = (value === "center" || value === "right" ? value : "left") as Align;
  return (
    <Row label={t("marketing.builder.prop.align")}>
      <div className="inline-flex rounded-md border border-border p-0.5">
        {(Object.keys(ALIGN_ICONS) as Align[]).map((a) => {
          const Icon = ALIGN_ICONS[a];
          return (
            <button
              key={a}
              type="button"
              onClick={() => onChange(a)}
              title={t(`marketing.builder.align.${a}`)}
              className={cn(
                "rounded px-2 py-1 transition-colors",
                current === a ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:bg-accent",
              )}
            >
              <Icon className="size-3.5" />
            </button>
          );
        })}
      </div>
    </Row>
  );
}

/**
 * A colour, with an explicit "use the letter's" state.
 *
 * `inherited` is what the renderer falls back to when the stored value is
 * empty; it is shown in the swatch so the field is never blank and never lies
 * about what the block will look like.
 */
export function ColorField({
  label,
  value,
  inherited,
  onChange,
  clearable = true,
}: {
  label: string;
  value: unknown;
  inherited?: string;
  onChange: (v: string) => void;
  clearable?: boolean;
}) {
  const t = useT();
  const stored = String(value ?? "");
  const shown = stored || inherited || "#000000";
  return (
    <Row label={label}>
      <div className="flex items-center gap-1.5">
        <input
          type="color"
          value={shown}
          onChange={(e) => onChange(e.target.value)}
          className="h-8 w-9 cursor-pointer rounded-md border border-input bg-transparent p-0.5"
        />
        <Input
          className="h-8 flex-1 font-mono text-[11px]"
          value={stored}
          placeholder={inherited ?? ""}
          onChange={(e) => onChange(e.target.value)}
        />
        {clearable && stored && (
          <button
            type="button"
            onClick={() => onChange("")}
            title={t("marketing.builder.prop.useLetterColor")}
            className="rounded p-1.5 text-muted-foreground hover:bg-accent hover:text-foreground"
          >
            <RotateCcw className="size-3.5" />
          </button>
        )}
      </div>
    </Row>
  );
}

/** Top and bottom padding, side by side. Left and right are the card's job -
 *  a block that indents itself inside a 600px card runs out of room fast. */
export function SpacingField({
  top,
  bottom,
  onChange,
}: {
  top: unknown;
  bottom: unknown;
  onChange: (patch: { paddingTop?: number; paddingBottom?: number }) => void;
}) {
  const t = useT();
  return (
    <div className="grid grid-cols-2 gap-2">
      <NumberField
        label={t("marketing.builder.prop.paddingTop")}
        value={top}
        max={200}
        suffix="px"
        onChange={(v) => onChange({ paddingTop: v })}
      />
      <NumberField
        label={t("marketing.builder.prop.paddingBottom")}
        value={bottom}
        max={200}
        suffix="px"
        onChange={(v) => onChange({ paddingBottom: v })}
      />
    </div>
  );
}
